/* eslint-disable import/no-cycle */
import useUrlState from '@ahooksjs/use-url-state';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import matches from 'lodash/matches';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import trim from 'lodash/trim';
import values from 'lodash/values';
import zipObject from 'lodash/zipObject';
import { MutableRefObject, useMemo, useRef, useState } from 'react';
import { getString } from '.';
import { useEntityPath } from '../components/EntityLink';
import { useConfig } from '../config/config.context';
import { useTracking } from '../Context';
import { applyFilters } from '../core/filter/utils';
import { useMe } from '../profile/hooks';
import store from '../shared/Store';
import { replaceValues, urlJoin } from './stringUtils';

function generateLowerCaseOrder(field: string) {
  return (item: any) => {
    const v = item[field];
    if (typeof v === 'string') return v.toLowerCase();
    return v;
  };
}

type Entity = {
  _id: string;
  collection?: string;
} & Record<string, any>;

export type OrderBy = {
  field: string;
  order?: 'asc' | 'desc';
};

export type Filter = {
  dataKey: string;
  type?: string;
};

export type TimezoneDate = {
  timezone: string;
};

type CalendarEvent = any; // TODO

// eslint-disable-next-line import/prefer-default-export
export function useOrderBy<T>(items: T[], itemOrderBy?: OrderBy[]): T[] {
  return useMemo(() => {
    if (!itemOrderBy || itemOrderBy.length === 0) return items;

    const fields = itemOrderBy.map((o) => generateLowerCaseOrder(o.field));
    const orders = itemOrderBy.map((o) => o.order ?? 'asc');
    return orderBy(items, fields, orders);
  }, [items, itemOrderBy]);
}

export function usePreFilter<T>(data: T[], preFilters: Record<string, any>): T[] {
  const allFilters = useMemo(() => preFilters && pickBy(preFilters, (v) => !!v), [preFilters]);
  const result = useMemo(() => applyFilters(data, allFilters, []), [data, allFilters]);
  return result;
}

function isQueryStringFilter(filter: Filter): boolean {
  return !!filter.dataKey && filter.type !== 'router-param';
}

export function useFilter<T>(data: T, filterList: Filter[], defaultFilters = {}): T[] {
  const [currentFilters, setFilters] = useUrlState({});

  const allFilters = useMemo(() => {
    // Remove junk from url state
    const validUrlFilters = pick(
      currentFilters,
      filterList.map((f) => f.dataKey), // TODO: works for all filters ?
    );
    return pickBy({ ...defaultFilters, ...validUrlFilters }, (v) => v != null);
  }, [currentFilters, filterList, defaultFilters]);

  const result = useMemo(() => applyFilters(data, allFilters, filterList), [
    data,
    allFilters,
    filterList,
  ]);
  return [
    result,
    allFilters,
    (f: any) => {
      // Disable all other filters
      const keys = filterList.map((filter) => filter.dataKey).filter((v) => v);
      const queryParams = filterList.filter(isQueryStringFilter).map((filter) => filter.dataKey);
      const nullFilters = zipObject(
        keys,
        keys.map(() => null),
      );
      setFilters({ ...nullFilters, ...pick(f, queryParams) });
    },
  ];
}

function generateUrl(
  basename: string,
  lang: string,
  canonicalPath: string,
  item: any,
  externalLink: boolean,
): string {
  if (!canonicalPath) return externalLink ? window.location.href : '';

  const baseUrlValue = externalLink ? `${window.location.protocol}://${window.location.host}` : '';
  const path = urlJoin([
    externalLink ? basename : '',
    replaceValues(canonicalPath, {
      ...item,
      lang,
    }),
  ]);
  return `${baseUrlValue}/${trim(path, '/')}`;
}

export const useEntityUrl = (entity: Entity, externalLink = true): string => {
  const { basename, lang, screens } = useConfig();
  const { path } = useEntityPath(entity);
  return useMemo(() => {
    return generateUrl(basename, lang, path, entity, externalLink);
  }, [basename, lang, entity, screens, externalLink]);
};

export function entityForEvent(event: CalendarEvent): Entity {
  if (event.type === 'appointment') {
    return { kind: 'users', ...event.group };
  }
  return event;
}

export const useCalendarEventLink = (
  event: CalendarEvent,
  externalLink = true,
): string | undefined => {
  const { lang, screens } = useConfig();
  const url = useEntityUrl(entityForEvent(event), externalLink);
  if (event.workshopId && event._id) {
    // Special treatment for workshop sessions ?
    if (screens.workshop?.ical?.url) {
      return replaceValues(screens.workshop?.ical?.url, { ...event, lang, userId: store.userId });
    }
  }
  return url;
};

export const useWorkshopTracker = (tag: string, entity: Entity): ((currentReplay: any) => void) => {
  const hasClicked = useRef(false);
  const { trackEvent } = useTracking();

  function onInferredClick(currentReplay: any) {
    // Only track the first click to know if launched
    if (!hasClicked.current) {
      trackEvent(tag, { workshop: entity, replay: currentReplay });
      hasClicked.current = true;
    }
  }

  return onInferredClick;
};

export function getTagLabel(type: string, key: string, value: string | number): string {
  if (!value) return '';
  return getString(`${type}.${key}.${value}`) || value;
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function useEntityTags(entity: Entity, type: string, tagFields: string[]): string[] {
  return useMemo(() => {
    return flatten(
      tagFields
        .map((field) => {
          const value = get(entity, field);
          if (!value) return null;
          const formattedValue = Array.isArray(value) ? value : [value];
          return formattedValue.map((v) => getTagLabel(type, field, v));
        })
        .filter(notEmpty),
    );
  }, [entity, type, tagFields]);
}

export function usePagination(
  items: any[],
  maxItems?: number,
): { totalPages?: number; pageItems: any[]; setActivePage?: (page: number) => void } {
  const [activePage, setActivePage] = useState(1);
  return useMemo(() => {
    if (!maxItems || !items) return { pageItems: items };
    const totalPages = Math.ceil(items?.length / (maxItems || 1));
    const startIndex = (activePage - 1) * maxItems;
    const pageItems = maxItems ? items.slice(startIndex, startIndex + maxItems) : items;
    return { totalPages, pageItems, setActivePage };
  }, [items, activePage, maxItems]);
}

export function usePageFromEntity(entity: Entity): any {
  const { screens } = useConfig();
  return values(screens).find((page) => {
    const { path, entities } = page;
    return path && entities && entities.find((pageEntity: any) => matches(pageEntity)(entity));
  });
}

export function useUserTimezone(): any {
  const { timezones, defaultTimezone } = useConfig();
  const user = useMe();
  return useMemo(() => {
    if (!timezones?.length) return defaultTimezone || null;
    return get(user, 'timezone', defaultTimezone);
  }, [user, timezones, defaultTimezone]);
}

function findUserTimezone(
  userTimezone?: string | null,
  timezoneDates?: TimezoneDate[],
): TimezoneDate | undefined {
  if (!timezoneDates || !userTimezone) return undefined;
  return timezoneDates.find((d) => d.timezone === userTimezone);
}

export function useUserTimezoneInfo(
  timezoneValues: TimezoneDate[] | undefined,
  defaultValue: Record<string, any>,
): any {
  const { timezones, defaultTimezone } = useConfig();
  const user = useMe();
  const userTimezone = useMemo(() => {
    if (!timezones?.length) return defaultTimezone || null;
    return get(user, 'timezone', defaultTimezone);
  }, [user, timezones, defaultTimezone]);

  const timezoneData = findUserTimezone(userTimezone, timezoneValues);
  return { ...defaultValue, ...timezoneData };
}

export const useFunctionRef = <T>(fun: T): MutableRefObject<T> => {
  const functionRef = useRef<T>(fun);
  functionRef.current = fun;
  return functionRef;
};
